import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { projectApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Plus,
  Search,
  MapPin,
  DollarSign,
  Calendar,
  Eye,
  Share2,
  FolderKanban,
  User,
  CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';

const PROJECT_PHASES = ['Design', 'Permit', 'Demo', 'Framing', 'Rough-ins', 'Drywall', 'Finishes', 'Final Inspection'];

const formatCurrency = (value) => {
  if (!value) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  }).format(value);
};

const getProgressPercent = (project) => {
  // Small projects track tasks; standard projects track phases (mirrors ProjectDetail)
  if (project.project_size === 'small') {
    const tasks = project.tasks || [];
    if (tasks.length === 0) return 0;
    return Math.round((tasks.filter((t) => t.completed).length / tasks.length) * 100);
  }
  const phases = project.phases || [];
  if (phases.length === 0) return 0;
  return Math.round((phases.filter((p) => p.completed).length / phases.length) * 100);
};

export default function Projects() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all'); // 'all' | 'mine'
  const [showCompletedPaid, setShowCompletedPaid] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await projectApi.getAll();
      setProjects(response.data);
    } catch (error) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const copyShareLink = async (shareToken) => {
    const url = `${window.location.origin}/portal/${shareToken}`;
    
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
        toast.success('Client portal link copied!');
      } else {
        throw new Error('Clipboard API not available');
      }
    } catch (err) {
      // Fallback for environments where Clipboard API is blocked
      try {
        const textArea = document.createElement('textarea');
        textArea.value = url;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        
        if (successful) {
          toast.success('Client portal link copied!');
        } else {
          toast.info(`Share link: ${url}`, { duration: 10000 });
        }
      } catch (fallbackErr) {
        toast.info(`Share link: ${url}`, { duration: 10000 });
      }
    }
  };

  const completedPaidCount = projects.filter(
    (p) => p.status === 'completed' && p.fully_paid
  ).length;

  const filteredProjects = projects.filter((project) => {
    const matchesSearch =
      project.name.toLowerCase().includes(search.toLowerCase()) ||
      project.client_name.toLowerCase().includes(search.toLowerCase()) ||
      project.address?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || project.status === statusFilter;
    const matchesAssignee = assigneeFilter === 'all' || project.assigned_manager_id === user?.id;
    // Hide completed & fully-paid jobs by default (unless explicitly showing them or filtering to completed)
    const hiddenAsSettled =
      !showCompletedPaid &&
      statusFilter !== 'completed' &&
      project.status === 'completed' &&
      project.fully_paid;
    return matchesSearch && matchesStatus && matchesAssignee && !hiddenAsSettled;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="loading-spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="projects-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Projects</h1>
          <p className="text-muted-foreground">Track your active construction projects</p>
        </div>
        <Link to="/projects/new">
          <Button className="rounded-sm bg-slate-900 hover:bg-slate-800 btn-press" data-testid="new-project-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Project
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search projects..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 rounded-sm"
            data-testid="project-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px] rounded-sm" data-testid="status-filter">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Projects</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="on_hold">On Hold</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant={assigneeFilter === 'mine' ? 'default' : 'outline'}
          className="rounded-sm"
          onClick={() => setAssigneeFilter(assigneeFilter === 'mine' ? 'all' : 'mine')}
          data-testid="my-projects-toggle"
        >
          <User className="h-4 w-4 mr-2" />
          My Projects
        </Button>
        {completedPaidCount > 0 && (
          <Button
            variant={showCompletedPaid ? 'default' : 'outline'}
            className="rounded-sm"
            onClick={() => setShowCompletedPaid((v) => !v)}
            data-testid="show-completed-paid-toggle"
          >
            <CheckCircle2 className="h-4 w-4 mr-2" />
            {showCompletedPaid ? 'Hide' : 'Show'} Completed & Paid ({completedPaidCount})
          </Button>
        )}
      </div>

      {/* Projects Grid */}
      {filteredProjects.length === 0 ? (
        <Card className="rounded-sm">
          <CardContent className="py-16 text-center">
            <FolderKanban className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-semibold mb-2">No projects yet</h3>
            <p className="text-muted-foreground mb-4">
              Create your first project to start tracking construction progress
            </p>
            <Link to="/projects/new">
              <Button className="rounded-sm">Create Project</Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map((project) => {
            const progress = getProgressPercent(project);
            const isSmall = project.project_size === 'small';
            const taskCount = (project.tasks || []).length;
            const taskDone = (project.tasks || []).filter((t) => t.completed).length;
            return (
              <Card
                key={project.id}
                className="rounded-sm hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/projects/${project.id}`)}
                data-testid={`project-card-${project.id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{project.name}</CardTitle>
                      <p className="text-sm text-muted-foreground">{project.client_name}</p>
                    </div>
                    <Badge
                      variant={project.status === 'active' ? 'default' : 'secondary'}
                      className={
                        project.status === 'active'
                          ? 'bg-green-700'
                          : project.status === 'completed'
                          ? 'bg-slate-900'
                          : ''
                      }
                    >
                      {project.status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Progress */}
                  <div>
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-muted-foreground">Progress</span>
                      <span className="font-medium">{progress}%</span>
                    </div>
                    <Progress value={progress} className="h-2" />
                    <p className="text-xs text-muted-foreground mt-1">
                      {isSmall
                        ? `${taskDone}/${taskCount} tasks complete`
                        : `Current: ${project.current_phase}`}
                    </p>
                  </div>

                  {/* Details */}
                  <div className="space-y-2 text-sm">
                    {project.address && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <MapPin className="h-4 w-4" strokeWidth={1.5} />
                        <span className="truncate">
                          {project.address}, {project.city}, {project.state}
                        </span>
                      </div>
                    )}
                    {project.contract_value && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <DollarSign className="h-4 w-4" strokeWidth={1.5} />
                        <span>{formatCurrency(project.contract_value)}</span>
                      </div>
                    )}
                    {project.estimated_end_date && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Calendar className="h-4 w-4" strokeWidth={1.5} />
                        <span>
                          Est. completion:{' '}
                          {new Date(project.estimated_end_date).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                          })}
                        </span>
                      </div>
                    )}
                    {project.assigned_manager_name && (
                      <div className="flex items-center gap-2 text-blue-600" data-testid={`project-assignee-${project.id}`}>
                        <User className="h-4 w-4" strokeWidth={1.5} />
                        <span className="truncate">{project.assigned_manager_name}</span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 rounded-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/projects/${project.id}`);
                      }}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        copyShareLink(project.share_token);
                      }}
                      data-testid={`share-project-${project.id}`}
                    >
                      <Share2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active</p>
                <p className="text-2xl font-bold">
                  {projects.filter((p) => p.status === 'active').length}
                </p>
              </div>
              <div className="h-10 w-10 bg-green-100 rounded-sm flex items-center justify-center">
                <FolderKanban className="h-5 w-5 text-green-700" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Completed</p>
                <p className="text-2xl font-bold">
                  {projects.filter((p) => p.status === 'completed').length}
                </p>
              </div>
              <div className="h-10 w-10 bg-slate-100 rounded-sm flex items-center justify-center">
                <FolderKanban className="h-5 w-5 text-slate-700" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Value</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(
                    projects.reduce((sum, p) => sum + (p.contract_value || 0), 0)
                  )}
                </p>
              </div>
              <div className="h-10 w-10 bg-orange-100 rounded-sm flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-orange-500" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
